import { useState, type ReactNode } from 'react';
import type {
  DashboardBuybackPoint,
  DashboardGranularity,
  DashboardStatus,
  DashboardStatusValues,
} from '../../api/dashboard';
import { formatDashboardCompact } from './formatters';

export type ChartValuePoint = {
  label: string;
  value: number;
  /** Разбивка по дням для совмещённого часового фрейма. */
  details?: Array<{ day: string; value: number }>;
};

export type ChartGranularity = DashboardGranularity;

/** Единая высота области графика для всех карточек. */
const PLOT_HEIGHT_CLASS = 'h-72';
const PLOT_HEIGHT_PX = 288;
const PLOT_HEIGHT_SM_CLASS = 'h-40';
const PLOT_HEIGHT_SM_PX = 160;
const PLOT_HEIGHT_LG_CLASS = 'h-[28rem]';
const PLOT_HEIGHT_LG_PX = 448;
const Y_AXIS_WIDTH_CLASS = 'w-14';
const X_AXIS_HEIGHT_CLASS = 'h-4';

type ChartSize = 'md' | 'sm' | 'lg';

function plotHeight(size: ChartSize = 'md') {
  if (size === 'sm') return { className: PLOT_HEIGHT_SM_CLASS, px: PLOT_HEIGHT_SM_PX };
  if (size === 'lg') return { className: PLOT_HEIGHT_LG_CLASS, px: PLOT_HEIGHT_LG_PX };
  return { className: PLOT_HEIGHT_CLASS, px: PLOT_HEIGHT_PX };
}

const statusMeta: Array<{ key: DashboardStatus; label: string; className: string; swatch: string }> = [
  { key: 'closed', label: 'Оплачено', className: 'bg-emerald-500', swatch: 'bg-emerald-500' },
  { key: 'canceled', label: 'Отменено', className: 'bg-amber-400', swatch: 'bg-amber-400' },
  { key: 'rejected', label: 'Отклонено', className: 'bg-rose-500', swatch: 'bg-rose-500' },
  { key: 'other', label: 'В работе', className: 'bg-slate-400', swatch: 'bg-slate-400' },
];

function chartMax(values: number[]): number {
  const max = Math.max(0, ...values.map((value) => Number(value) || 0));
  return max > 0 ? max : 1;
}

function isHourlyLabel(label: string): boolean {
  return /^\d{1,2}:\d{2}$/.test(label) || /^\d{2}$/.test(label);
}

/** Центр столбца i из n — для bar/stacked. */
function columnCenterPercent(index: number, count: number): number {
  if (count <= 1) return 50;
  return ((index + 0.5) / count) * 100;
}

/** Край–край: первая точка слева, последняя справа — для line. */
function edgePercent(index: number, count: number): number {
  if (count <= 1) return 50;
  return (index / (count - 1)) * 100;
}

function xPercent(index: number, count: number, mode: 'edge' | 'column'): number {
  return mode === 'edge' ? edgePercent(index, count) : columnCenterPercent(index, count);
}

function AxisLabels({
  labels,
  granularity,
  xMode = 'column',
}: {
  labels: string[];
  granularity?: ChartGranularity;
  xMode?: 'edge' | 'column';
}) {
  if (labels.length === 0) return null;

  const hourly =
    granularity === 'hour' || (granularity !== 'day' && labels.every(isHourlyLabel));

  if (hourly && labels.length === 24) {
    const marks = [
      { hour: 0, text: '00' },
      { hour: 6, text: '06' },
      { hour: 12, text: '12' },
      { hour: 18, text: '18' },
      { hour: 24, text: '24' },
    ];
    return (
      <div className={`relative mt-1.5 ${X_AXIS_HEIGHT_CLASS} text-[10px] font-semibold tabular-nums text-slate-500`}>
        {marks.map((mark) => {
          const left =
            mark.hour === 24
              ? 100
              : xPercent(Math.min(mark.hour, labels.length - 1), labels.length, xMode);
          return (
            <span
              key={mark.text}
              className="absolute -translate-x-1/2 whitespace-nowrap"
              style={{ left: `${left}%` }}
            >
              {mark.text}
            </span>
          );
        })}
      </div>
    );
  }

  const last = Math.max(0, labels.length - 1);
  const positions =
    hourly && last > 24
      ? [...new Set([0, Math.round(last / 3), Math.round((2 * last) / 3), last])]
      : [...new Set([0, Math.floor(last / 2), last])];
  return (
    <div className={`relative mt-1.5 ${X_AXIS_HEIGHT_CLASS} text-[10px] font-semibold tabular-nums text-slate-500`}>
      {positions.map((index) => (
        <span
          key={`${index}-${labels[index]}`}
          className="absolute -translate-x-1/2 whitespace-nowrap"
          style={{ left: `${xPercent(index, labels.length, xMode)}%` }}
        >
          {labels[index]}
        </span>
      ))}
    </div>
  );
}

function YAxis({
  max,
  formatValue,
}: {
  max: number;
  formatValue: (value: number) => string;
}) {
  // 0 не показываем — нижняя граница и так понятна.
  const ticks = [1, 0.75, 0.5, 0.25];
  return (
    <div
      className={`relative h-full ${Y_AXIS_WIDTH_CLASS} shrink-0 pr-2 text-right text-[10px] font-semibold tabular-nums text-slate-500`}
    >
      {ticks.map((ratio) => (
        <span
          key={ratio}
          className={`absolute right-2 whitespace-nowrap ${
            ratio === 1 ? 'top-0' : '-translate-y-1/2'
          }`}
          style={ratio === 1 ? undefined : { top: `${(1 - ratio) * 100}%` }}
        >
          {formatValue(max * ratio)}
        </span>
      ))}
    </div>
  );
}

function TooltipBubble({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-lg border border-slate-700 bg-slate-950 px-2.5 py-1.5 text-[11px] leading-snug text-white shadow-2xl ${
        className || 'whitespace-nowrap'
      }`}
    >
      {children}
    </div>
  );
}

function statusTotal(values: DashboardStatusValues, enabled: Set<DashboardStatus>): number {
  return statusMeta.reduce(
    (sum, status) =>
      sum + (enabled.has(status.key) ? Math.max(0, Number(values[status.key]) || 0) : 0),
    0,
  );
}

function StatusDotsRow({
  values,
  enabledStatuses,
  formatValue,
}: {
  values: DashboardStatusValues;
  enabledStatuses: Set<DashboardStatus>;
  formatValue: (value: number) => string;
}) {
  const statuses = statusMeta.filter(
    (status) => enabledStatuses.has(status.key) && Math.max(0, Number(values[status.key]) || 0) > 0,
  );
  if (statuses.length === 0) {
    return <span className="font-mono text-white/50">0</span>;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5">
      {statuses.map((status) => (
        <span key={status.key} className="inline-flex items-center gap-1 font-mono font-semibold text-white">
          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${status.swatch}`} title={status.label} />
          {formatValue(values[status.key])}
        </span>
      ))}
    </span>
  );
}

/** Один день — полный список; несколько — строка на день с точками статусов. */
function StackedHourTooltip({
  label,
  values,
  details,
  enabledStatuses,
  formatValue,
}: {
  label: string;
  values: DashboardStatusValues;
  details?: Array<{ day: string; values: DashboardStatusValues }>;
  enabledStatuses: Set<DashboardStatus>;
  formatValue: (value: number) => string;
}) {
  const statuses = statusMeta.filter((status) => enabledStatuses.has(status.key));
  const multiDay = Boolean(details && details.length > 1);
  const total = statusTotal(values, enabledStatuses);

  if (!multiDay) {
    return (
      <>
        <div className="mb-1 font-bold text-white">{label}</div>
        {statuses.map((status) => (
          <div key={status.key} className="flex items-center justify-between gap-4 text-white">
            <span className="inline-flex items-center gap-1.5 font-medium">
              <span className={`h-1.5 w-1.5 rounded-full ${status.swatch}`} />
              {status.label}
            </span>
            <span className="font-mono font-semibold">{formatValue(values[status.key])}</span>
          </div>
        ))}
        <div className="mt-1 flex items-center justify-between gap-4 border-t border-white/20 pt-1 text-white">
          <span className="font-bold">Итого</span>
          <span className="font-mono font-bold">{formatValue(total)}</span>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex items-baseline justify-between gap-4">
        <span className="font-bold text-white">{label}</span>
        <span className="font-mono font-bold text-white">{formatValue(total)}</span>
      </div>
      <div className="mt-0.5">
        <StatusDotsRow values={values} enabledStatuses={enabledStatuses} formatValue={formatValue} />
      </div>
      <div className="mt-1 space-y-1 border-t border-white/20 pt-1">
        {(details ?? []).map((detail) => (
          <div key={detail.day} className="flex items-start justify-between gap-3 text-white">
            <span className="shrink-0 pt-px font-medium text-white/70">{detail.day}</span>
            <StatusDotsRow
              values={detail.values}
              enabledStatuses={enabledStatuses}
              formatValue={formatValue}
            />
          </div>
        ))}
      </div>
    </>
  );
}

/** Сетка как у bar/line — одна разметка на все графики. */
function PlotGrid({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`relative isolate min-w-0 flex-1 overflow-visible border-b border-slate-300 ${className}`}
      style={{
        backgroundImage: [
          'linear-gradient(to right, transparent 0, transparent 100%)',
          'linear-gradient(#e2e8f0, #e2e8f0)',
          'linear-gradient(#e2e8f0, #e2e8f0)',
          'linear-gradient(#e2e8f0, #e2e8f0)',
          'linear-gradient(#e2e8f0, #e2e8f0)',
        ].join(','),
        backgroundSize: '100% 100%, 100% 1px, 100% 1px, 100% 1px, 100% 1px',
        backgroundPosition: '0 0, 0 0, 0 25%, 0 50%, 0 75%',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {children}
    </div>
  );
}

function defaultAxisFormatter(value: number): string {
  return formatDashboardCompact(value);
}

function ChartShell({
  max,
  formatAxisValue,
  labels,
  granularity,
  xMode = 'column',
  size = 'md',
  children,
}: {
  max: number;
  formatAxisValue: (value: number) => string;
  labels: string[];
  granularity?: ChartGranularity;
  xMode?: 'edge' | 'column';
  size?: ChartSize;
  children: ReactNode;
}) {
  const plot = plotHeight(size);
  return (
    <div>
      <div className={`flex ${plot.className}`}>
        <YAxis max={max} formatValue={formatAxisValue} />
        {children}
      </div>
      <div className="ml-14">
        <AxisLabels labels={labels} granularity={granularity} xMode={xMode} />
      </div>
    </div>
  );
}

function HoverTooltip({
  index,
  count,
  topPercent,
  xMode = 'column',
  bubbleClassName,
  children,
}: {
  index: number;
  count: number;
  /** 0 = верх графика, 100 = низ */
  topPercent: number;
  xMode?: 'edge' | 'column';
  bubbleClassName?: string;
  children: ReactNode;
}) {
  const leftPercent = xPercent(index, count, xMode);
  // У высоких свечей тултип уезжает вверх — ставим под вершину.
  const placeBelow = topPercent < 28;
  let xTransform = 'translateX(-50%)';
  if (index === 0) xTransform = 'translateX(0)';
  else if (index === count - 1) xTransform = 'translateX(-100%)';
  const yTransform = placeBelow ? 'translateY(8px)' : 'translateY(calc(-100% - 8px))';

  return (
    <div
      className="pointer-events-none absolute z-[100]"
      style={{
        left: `${leftPercent}%`,
        top: `${Math.max(0, Math.min(100, topPercent))}%`,
        transform: `${xTransform} ${yTransform}`,
      }}
    >
      <TooltipBubble className={bubbleClassName}>{children}</TooltipBubble>
    </div>
  );
}

export function EmptyChart() {
  return (
    <div className={`flex ${PLOT_HEIGHT_CLASS} items-center justify-center text-sm text-slate-400`}>
      Нет данных
    </div>
  );
}

export function StatusLegend({
  enabled,
  onToggle,
}: {
  enabled: Set<DashboardStatus>;
  onToggle: (key: DashboardStatus) => void;
}) {
  return (
    <div className="flex h-6 flex-nowrap items-center gap-0.5 overflow-x-auto">
      {statusMeta.map((status) => {
        const active = enabled.has(status.key);
        return (
          <button
            key={status.key}
            type="button"
            aria-pressed={active}
            onClick={() => onToggle(status.key)}
            className={`inline-flex h-6 shrink-0 items-center gap-1 rounded px-1.5 text-[10px] font-semibold leading-none transition ${
              active ? 'bg-slate-100 text-slate-800' : 'bg-transparent text-slate-400 opacity-70'
            }`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${status.swatch} ${active ? '' : 'grayscale'}`} />
            {status.label}
          </button>
        );
      })}
    </div>
  );
}

export function useStatusLegendState(initial?: DashboardStatus[]) {
  const [enabled, setEnabled] = useState<Set<DashboardStatus>>(
    () => new Set(initial ?? statusMeta.map((status) => status.key)),
  );
  const toggle = (key: DashboardStatus) => {
    setEnabled((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  return { enabled, toggle };
}

export function BarChart({
  data,
  formatValue,
  formatAxisValue = defaultAxisFormatter,
  colorClass = 'bg-gradient-to-t from-rose-700 to-rose-400',
  granularity,
  size = 'md',
}: {
  data: ChartValuePoint[];
  formatValue: (value: number) => string;
  formatAxisValue?: (value: number) => string;
  colorClass?: string;
  granularity?: ChartGranularity;
  size?: ChartSize;
}) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  if (data.length === 0) return <EmptyChart />;
  const max = chartMax(data.map((point) => point.value));
  const hovered = hoveredIndex != null ? data[hoveredIndex] : null;
  const hoveredValue = hovered ? Math.max(0, Number(hovered.value) || 0) : 0;
  const hoveredTop = hoveredValue > 0 ? 100 - (hoveredValue / max) * 100 : 50;

  return (
    <ChartShell
      max={max}
      formatAxisValue={formatAxisValue}
      labels={data.map((point) => point.label)}
      granularity={granularity}
      size={size}
    >
        <PlotGrid className="flex items-end gap-px">
          {data.map((point, index) => {
            const value = Math.max(0, Number(point.value) || 0);
            const height = value > 0 ? (value / max) * 100 : 0;
            return (
              <div
                key={`${point.label}-${index}`}
                className="relative flex h-full min-w-0 flex-1 items-end"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex((current) => (current === index ? null : current))}
              >
              {value > 0 ? (
                <div
                  className={`w-full min-w-[2px] rounded-t-[2px] transition-opacity ${colorClass} ${
                    hoveredIndex === index ? 'opacity-100' : hoveredIndex != null ? 'opacity-55' : ''
                  }`}
                  style={{ height: `${height}%` }}
                />
              ) : null}
            </div>
          );
        })}
        {hoveredIndex != null && hovered ? (
          <HoverTooltip index={hoveredIndex} count={data.length} topPercent={hoveredTop}>
            <div className="font-bold text-white">{hovered.label}</div>
            <div className="mt-0.5 font-mono font-semibold text-white">{formatValue(hoveredValue)}</div>
            {hovered.details && hovered.details.length > 1 ? (
              <div className="mt-1.5 space-y-0.5 border-t border-white/20 pt-1.5">
                {hovered.details.map((detail) => (
                  <div key={detail.day} className="flex items-center justify-between gap-5 text-white">
                    <span className="font-medium text-white/80">{detail.day}</span>
                    <span className="font-mono font-semibold">{formatValue(detail.value)}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </HoverTooltip>
        ) : null}
      </PlotGrid>
    </ChartShell>
  );
}

export function StackedBarChart({
  data,
  formatValue,
  formatAxisValue = defaultAxisFormatter,
  legend = true,
  enabledStatuses: enabledStatusesProp,
  onToggleStatus,
  granularity,
}: {
  data: Array<{
    label: string;
    values: DashboardStatusValues;
    details?: Array<{ day: string; values: DashboardStatusValues }>;
  }>;
  formatValue: (value: number) => string;
  formatAxisValue?: (value: number) => string;
  legend?: boolean;
  enabledStatuses?: Set<DashboardStatus>;
  onToggleStatus?: (key: DashboardStatus) => void;
  granularity?: ChartGranularity;
}) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [internalEnabled, setInternalEnabled] = useState<Set<DashboardStatus>>(
    () => new Set(statusMeta.map((status) => status.key)),
  );
  const enabledStatuses = enabledStatusesProp ?? internalEnabled;
  const toggleStatus =
    onToggleStatus ??
    ((key: DashboardStatus) => {
      setInternalEnabled((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    });

  if (data.length === 0) return <EmptyChart />;
  const totals = data.map((point) =>
    statusMeta.reduce(
      (sum, status) =>
        sum + (enabledStatuses.has(status.key) ? Math.max(0, Number(point.values[status.key]) || 0) : 0),
      0,
    ),
  );
  const max = chartMax(totals);
  const hovered = hoveredIndex != null ? data[hoveredIndex] : null;
  const hoveredTotal = hoveredIndex != null ? totals[hoveredIndex] : 0;
  const hoveredTop = hoveredTotal > 0 ? 100 - (hoveredTotal / max) * 100 : 50;

  return (
    <div>
      {legend ? (
        <div className="mb-3">
          <StatusLegend enabled={enabledStatuses} onToggle={toggleStatus} />
        </div>
      ) : null}
      <ChartShell
        max={max}
        formatAxisValue={formatAxisValue}
        labels={data.map((point) => point.label)}
        granularity={granularity}
      >
        <PlotGrid className="flex items-end gap-px">
          {data.map((point, index) => (
            <div
              key={`${point.label}-${index}`}
              className="relative flex h-full min-w-0 flex-1 items-end"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex((current) => (current === index ? null : current))}
            >
              {totals[index] > 0 ? (
                <div
                  className={`flex w-full min-w-[2px] flex-col-reverse overflow-hidden rounded-t-[2px] transition-opacity ${
                    hoveredIndex === index ? 'opacity-100' : hoveredIndex != null ? 'opacity-55' : ''
                  }`}
                  style={{ height: `${(totals[index] / max) * 100}%` }}
                >
                  {statusMeta.map((status) => {
                    const value = Math.max(0, Number(point.values[status.key]) || 0);
                    return enabledStatuses.has(status.key) && value > 0 ? (
                      <div
                        key={status.key}
                        className={status.className}
                        style={{ height: `${(value / totals[index]) * 100}%` }}
                      />
                    ) : null;
                  })}
                </div>
              ) : null}
            </div>
          ))}
          {hoveredIndex != null && hovered ? (
            <HoverTooltip index={hoveredIndex} count={data.length} topPercent={hoveredTop}>
              <StackedHourTooltip
                label={hovered.label}
                values={hovered.values}
                details={hovered.details}
                enabledStatuses={enabledStatuses}
                formatValue={formatValue}
              />
            </HoverTooltip>
          ) : null}
        </PlotGrid>
      </ChartShell>
    </div>
  );
}

export function LineChart({
  data,
  formatValue,
  formatAxisValue = defaultAxisFormatter,
  color = '#2563eb',
  ceiling: _ceiling,
  granularity,
  size = 'md',
}: {
  data: ChartValuePoint[];
  formatValue: (value: number) => string;
  formatAxisValue?: (value: number) => string;
  color?: string;
  ceiling?: number;
  granularity?: ChartGranularity;
  size?: ChartSize;
}) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  if (data.length === 0) return <EmptyChart />;
  void _ceiling;
  const width = 900;
  const height = plotHeight(size).px;
  const max = chartMax(data.map((point) => point.value));
  const points = data.map((point, index) => {
    // Край–край: 13.07 слева, 17.07 справа, без пустых полей по бокам.
    const x = (edgePercent(index, data.length) / 100) * width;
    const y = (1 - Math.max(0, Number(point.value) || 0) / max) * height;
    return { ...point, x, y };
  });
  const polyline = points.map((point) => `${point.x},${point.y}`).join(' ');
  const area = `0,${height} ${polyline} ${width},${height}`;
  const hovered = hoveredIndex != null ? points[hoveredIndex] : null;

  const pickIndexFromClientX = (clientX: number, target: HTMLElement) => {
    const rect = target.getBoundingClientRect();
    if (rect.width <= 0 || data.length <= 1) return 0;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return Math.round(ratio * (data.length - 1));
  };

  return (
    <ChartShell
      max={max}
      formatAxisValue={formatAxisValue}
      labels={data.map((point) => point.label)}
      granularity={granularity}
      xMode="edge"
      size={size}
    >
      <PlotGrid>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
          preserveAspectRatio="none"
          role="img"
          aria-label="График значений по времени"
        >
          <polygon points={area} fill={color} opacity="0.1" />
          <polyline
            points={polyline}
            fill="none"
            stroke={color}
            strokeWidth="3"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          {points.map((point, index) => (
            <circle
              key={`${point.label}-${index}`}
              cx={point.x}
              cy={point.y}
              r={hoveredIndex === index ? 6 : 4}
              fill={color}
            />
          ))}
        </svg>

        {/* Умное наведение: ближайшая точка по X, без попадания точно в кружок. */}
        <div
          className="absolute inset-0 z-10 cursor-crosshair"
          onMouseLeave={() => setHoveredIndex(null)}
          onMouseMove={(event) => {
            setHoveredIndex(pickIndexFromClientX(event.clientX, event.currentTarget));
          }}
        >
          {hoveredIndex != null ? (
            <div
              className="pointer-events-none absolute inset-y-0 w-px bg-slate-500/45"
              style={{ left: `${edgePercent(hoveredIndex, points.length)}%` }}
            />
          ) : null}
        </div>

        {hovered && hoveredIndex != null ? (
          <HoverTooltip
            index={hoveredIndex}
            count={points.length}
            topPercent={(hovered.y / height) * 100}
            xMode="edge"
          >
            <div className="font-bold text-white">{hovered.label}</div>
            <div className="mt-0.5 font-mono font-semibold text-white">{formatValue(hovered.value)}</div>
            {hovered.details && hovered.details.length > 1 ? (
              <div className="mt-1.5 space-y-0.5 border-t border-white/20 pt-1.5">
                {hovered.details.map((detail) => (
                  <div key={detail.day} className="flex items-center justify-between gap-5 text-white">
                    <span className="font-medium text-white/80">{detail.day}</span>
                    <span className="font-mono font-semibold">{formatValue(detail.value)}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </HoverTooltip>
        ) : null}
      </PlotGrid>
    </ChartShell>
  );
}

export const MULTI_LINE_PALETTE = [
  '#2563eb',
  '#dc2626',
  '#059669',
  '#d97706',
  '#7c3aed',
  '#0891b2',
  '#db2777',
  '#65a30d',
  '#ea580c',
  '#4f46e5',
  '#0d9488',
  '#9333ea',
];

export type MultiLineSeries = {
  id: string;
  name: string;
  color: string;
  values: number[];
  visible: boolean;
};

/** Плавная кривая без ухода ниже/выше соседних точек (monotone cubic). */
function buildMonotonePath(points: Array<{ x: number; y: number }>): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const n = points.length;
  const dx: number[] = [];
  const dy: number[] = [];
  const m: number[] = [];

  for (let i = 0; i < n - 1; i += 1) {
    dx[i] = xs[i + 1] - xs[i];
    dy[i] = ys[i + 1] - ys[i];
    m[i] = dx[i] === 0 ? 0 : dy[i] / dx[i];
  }

  const slopes = new Array<number>(n);
  slopes[0] = m[0];
  slopes[n - 1] = m[n - 2];
  for (let i = 1; i < n - 1; i += 1) {
    if (m[i - 1] * m[i] <= 0) slopes[i] = 0;
    else slopes[i] = (m[i - 1] + m[i]) / 2;
  }

  for (let i = 0; i < n - 1; i += 1) {
    if (Math.abs(m[i]) < 1e-12) {
      slopes[i] = 0;
      slopes[i + 1] = 0;
      continue;
    }
    const a = slopes[i] / m[i];
    const b = slopes[i + 1] / m[i];
    const s = a * a + b * b;
    if (s > 9) {
      const t = 3 / Math.sqrt(s);
      slopes[i] = t * a * m[i];
      slopes[i + 1] = t * b * m[i];
    }
  }

  let d = `M ${xs[0]} ${ys[0]}`;
  for (let i = 0; i < n - 1; i += 1) {
    const x0 = xs[i];
    const x1 = xs[i + 1];
    const y0 = ys[i];
    const y1 = ys[i + 1];
    const cpx0 = x0 + dx[i] / 3;
    const cpy0 = y0 + (slopes[i] * dx[i]) / 3;
    const cpx1 = x1 - dx[i] / 3;
    const cpy1 = y1 - (slopes[i + 1] * dx[i]) / 3;
    d += ` C ${cpx0} ${cpy0}, ${cpx1} ${cpy1}, ${x1} ${y1}`;
  }
  return d;
}

export function MultiLineChart({
  labels,
  series,
  formatValue,
  formatAxisValue = defaultAxisFormatter,
  granularity,
  size = 'md',
  onToggleSeries,
}: {
  labels: string[];
  series: MultiLineSeries[];
  formatValue: (value: number) => string;
  formatAxisValue?: (value: number) => string;
  granularity?: ChartGranularity;
  size?: ChartSize;
  onToggleSeries?: (id: string, mode?: 'toggle' | 'solo') => void;
}) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const visibleSeries = series.filter((item) => item.visible);
  if (labels.length === 0 || series.length === 0) return <EmptyChart />;

  const width = 900;
  const height = plotHeight(size).px;
  const max = chartMax(visibleSeries.flatMap((item) => item.values));

  const pickIndexFromClientX = (clientX: number, target: HTMLElement) => {
    const rect = target.getBoundingClientRect();
    if (rect.width <= 0 || labels.length <= 1) return 0;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return Math.round(ratio * (labels.length - 1));
  };

  const seriesPoints = (values: number[]) =>
    values.map((value, index) => ({
      x: (edgePercent(index, labels.length) / 100) * width,
      y: (1 - Math.max(0, Number(value) || 0) / max) * height,
    }));

  const hoveredValues =
    hoveredIndex == null
      ? []
      : visibleSeries
          .map((item) => ({
            id: item.id,
            name: item.name,
            color: item.color,
            value: Number(item.values[hoveredIndex] ?? 0) || 0,
          }))
          .sort((a, b) => b.value - a.value);

  const topAtHover = hoveredValues[0]?.id ?? null;

  const tooltipTop =
    hoveredIndex == null || hoveredValues.length === 0
      ? 20
      : (1 - Math.max(...hoveredValues.map((item) => item.value)) / max) * 100;

  const lineStyle = (id: string) => {
    if (focusId != null) {
      const active = focusId === id;
      return {
        opacity: active ? 1 : 0.14,
        strokeWidth: active ? 3.5 : 1.75,
        dimmed: !active,
      };
    }
    if (hoveredIndex != null && topAtHover != null) {
      const active = id === topAtHover;
      return {
        opacity: active ? 1 : 0.22,
        strokeWidth: active ? 3.25 : 1.75,
        dimmed: !active,
      };
    }
    return { opacity: 0.92, strokeWidth: 2.4, dimmed: false };
  };

  return (
    <div className="flex min-w-0 gap-3">
      <div className="min-w-0 flex-1">
        <ChartShell
          max={max}
          formatAxisValue={formatAxisValue}
          labels={labels}
          granularity={granularity}
          xMode="edge"
          size={size}
        >
          <PlotGrid>
            <svg
              viewBox={`0 0 ${width} ${height}`}
              className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
              preserveAspectRatio="none"
              role="img"
              aria-label="Загруженность реквизитов"
            >
              {/* Сначала приглушённые, сверху активная — без «каши» на пересечениях. */}
              {[...visibleSeries]
                .sort((a, b) => {
                  const aActive = lineStyle(a.id).dimmed ? 0 : 1;
                  const bActive = lineStyle(b.id).dimmed ? 0 : 1;
                  return aActive - bActive;
                })
                .map((item) => {
                  const style = lineStyle(item.id);
                  const path = buildMonotonePath(seriesPoints(item.values));
                  return (
                    <g key={item.id} opacity={style.opacity}>
                      {!style.dimmed ? (
                        <path
                          d={path}
                          fill="none"
                          stroke="#ffffff"
                          strokeWidth={style.strokeWidth + 2.5}
                          strokeLinejoin="round"
                          strokeLinecap="round"
                          vectorEffect="non-scaling-stroke"
                        />
                      ) : null}
                      <path
                        d={path}
                        fill="none"
                        stroke={item.color}
                        strokeWidth={style.strokeWidth}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        vectorEffect="non-scaling-stroke"
                      />
                    </g>
                  );
                })}
              {hoveredIndex != null
                ? visibleSeries.map((item) => {
                    const style = lineStyle(item.id);
                    if (style.dimmed && focusId == null) return null;
                    const value = Number(item.values[hoveredIndex] ?? 0) || 0;
                    const x = (edgePercent(hoveredIndex, labels.length) / 100) * width;
                    const y = (1 - value / max) * height;
                    return (
                      <g key={`dot-${item.id}`} opacity={style.opacity}>
                        <circle cx={x} cy={y} r={6} fill="#fff" />
                        <circle cx={x} cy={y} r={4.2} fill={item.color} />
                      </g>
                    );
                  })
                : null}
            </svg>

            <div
              className="absolute inset-0 z-10 cursor-crosshair"
              onMouseLeave={() => setHoveredIndex(null)}
              onMouseMove={(event) => {
                setHoveredIndex(pickIndexFromClientX(event.clientX, event.currentTarget));
              }}
            >
              {hoveredIndex != null ? (
                <div
                  className="pointer-events-none absolute inset-y-0 w-px bg-slate-500/45"
                  style={{ left: `${edgePercent(hoveredIndex, labels.length)}%` }}
                />
              ) : null}
            </div>

            {hoveredIndex != null && hoveredValues.length > 0 ? (
              <HoverTooltip
                index={hoveredIndex}
                count={labels.length}
                topPercent={tooltipTop}
                xMode="edge"
                bubbleClassName="whitespace-normal max-w-[240px]"
              >
                <div className="mb-1 font-bold text-white">{labels[hoveredIndex]}</div>
                <div className="vnd-scroll max-h-44 space-y-0.5 overflow-y-auto">
                  {hoveredValues.map((item) => (
                    <div key={item.id} className="flex items-center justify-between gap-3 text-white">
                      <span className="inline-flex min-w-0 items-center gap-1.5">
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="truncate font-medium">{item.name}</span>
                      </span>
                      <span className="shrink-0 font-mono font-semibold">{formatValue(item.value)}</span>
                    </div>
                  ))}
                </div>
              </HoverTooltip>
            ) : null}
          </PlotGrid>
        </ChartShell>
      </div>

      <div className="vnd-scroll hidden w-40 shrink-0 flex-col gap-1 overflow-y-auto sm:flex sm:max-h-[28rem]">
        {series.map((item) => {
          const muted = !item.visible;
          const focused = focusId === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={(event) => {
                const solo = event.altKey || event.metaKey || event.ctrlKey;
                onToggleSeries?.(item.id, solo ? 'solo' : 'toggle');
              }}
              onMouseEnter={() => {
                if (item.visible) setFocusId(item.id);
              }}
              onMouseLeave={() => setFocusId(null)}
              className={`flex items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-[11px] font-medium transition ${
                muted
                  ? 'text-slate-400 line-through opacity-60'
                  : focused
                    ? 'bg-slate-100 text-slate-900'
                    : 'text-slate-700 hover:bg-slate-50'
              }`}
              title={`${item.name}\nКлик — скрыть/показать\n⌥ / Ctrl — только этот`}
            >
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: muted ? '#cbd5e1' : item.color }}
              />
              <span className="truncate">{item.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const MSK_OFFSET_MS = 3 * 60 * 60 * 1000;

function parseDashboardDateStartMs(dateYYYYMMDD: string): number {
  const [y, m, d] = dateYYYYMMDD.split('-').map(Number);
  return Date.UTC(y, (m || 1) - 1, d || 1) - MSK_OFFSET_MS;
}

function formatMskParts(isoOrDate: string | Date) {
  const date = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? '';
  return {
    date: `${part('year')}-${part('month')}-${part('day')}`,
    day: `${part('day')}.${part('month')}`,
    time: `${part('hour')}:${part('minute')}`,
    hour: Number(part('hour') || 0),
  };
}

function formatTooltipUsdt(value: number): string {
  return Number(value || 0).toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

function buybackTooltipText(point: DashboardBuybackPoint): string {
  return `#${point.id} - ${formatTooltipUsdt(point.realized_profit_usdt)} USDT`;
}

type BuybackCandle = {
  label: string;
  value: number;
  high: number;
  low: number;
  items: DashboardBuybackPoint[];
  details?: Array<{ day: string; value: number }>;
};

function eachDateYYYYMMDD(dateFrom: string, dateTo: string): string[] {
  const out: string[] = [];
  let at = parseDashboardDateStartMs(dateFrom);
  const end = parseDashboardDateStartMs(dateTo);
  const dayMs = 24 * 60 * 60 * 1000;
  while (at <= end) {
    const parts = formatMskParts(new Date(at));
    out.push(parts.date);
    at += dayMs;
  }
  return out;
}

function buildBuybackCandles(
  points: DashboardBuybackPoint[],
  dateFrom: string,
  dateTo: string,
  granularity: DashboardGranularity,
): BuybackCandle[] {
  const sameDay = dateFrom === dateTo;
  const useHour = granularity === 'hour' || sameDay;

  if (useHour) {
    const buckets = Array.from({ length: 24 }, (_, hour) => ({
      label: String(hour).padStart(2, '0'),
      items: [] as DashboardBuybackPoint[],
      daySums: new Map<string, number>(),
    }));
    for (const point of points) {
      const parts = formatMskParts(point.occurred_at);
      if (parts.hour < 0 || parts.hour > 23) continue;
      buckets[parts.hour].items.push(point);
      if (!sameDay) {
        buckets[parts.hour].daySums.set(
          parts.day,
          (buckets[parts.hour].daySums.get(parts.day) ?? 0) + (Number(point.realized_profit_usdt) || 0),
        );
      }
    }
    return buckets.map((bucket) => {
      const profits = bucket.items.map((item) => Number(item.realized_profit_usdt) || 0);
      const value = profits.reduce((sum, item) => sum + item, 0);
      return {
        label: bucket.label,
        value,
        high: profits.length ? Math.max(...profits) : 0,
        low: profits.length ? Math.min(...profits) : 0,
        items: bucket.items,
        details:
          !sameDay && bucket.daySums.size > 0
            ? [...bucket.daySums.entries()]
                .sort((a, b) => a[0].localeCompare(b[0], 'ru'))
                .map(([day, dayValue]) => ({ day, value: dayValue }))
            : undefined,
      };
    });
  }

  const dates = eachDateYYYYMMDD(dateFrom, dateTo);
  const buckets = dates.map((date) => {
    const [, m, d] = date.split('-');
    return {
      label: `${d}.${m}`,
      date,
      items: [] as DashboardBuybackPoint[],
    };
  });
  const byDate = new Map(buckets.map((bucket) => [bucket.date, bucket]));
  for (const point of points) {
    const parts = formatMskParts(point.occurred_at);
    const bucket = byDate.get(parts.date);
    if (bucket) bucket.items.push(point);
  }
  return buckets.map((bucket) => {
    const profits = bucket.items.map((item) => Number(item.realized_profit_usdt) || 0);
    const value = profits.reduce((sum, item) => sum + item, 0);
    return {
      label: bucket.label,
      value,
      high: profits.length ? Math.max(...profits) : 0,
      low: profits.length ? Math.min(...profits) : 0,
      items: bucket.items,
    };
  });
}

/** Свечи: час/день-бакеты, внутри — сумма профита всех откупов. */
export function BuybackScatterChart({
  points,
  dateFrom,
  dateTo,
  granularity,
  formatValue = formatDashboardCompact,
  formatAxisValue = defaultAxisFormatter,
}: {
  points: DashboardBuybackPoint[];
  dateFrom: string;
  dateTo: string;
  granularity: DashboardGranularity;
  formatValue?: (value: number) => string;
  formatAxisValue?: (value: number) => string;
}) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  if (!dateFrom || !dateTo) return <EmptyChart />;
  if (points.length === 0) return <EmptyChart />;

  const candles = buildBuybackCandles(points, dateFrom, dateTo, granularity);
  const max = chartMax(candles.map((candle) => Math.max(candle.value, candle.high)));
  const hovered = hoveredIndex != null ? candles[hoveredIndex] : null;
  const hoveredValue = hovered ? Math.max(0, Number(hovered.value) || 0) : 0;
  const hoveredTop = hoveredValue > 0 ? 100 - (hoveredValue / max) * 100 : 50;
  const chartGranularity: ChartGranularity =
    granularity === 'hour' || dateFrom === dateTo ? 'hour' : 'day';

  return (
    <ChartShell
      max={max}
      formatAxisValue={formatAxisValue}
      labels={candles.map((candle) => candle.label)}
      granularity={chartGranularity}
      size="md"
    >
      <PlotGrid className="flex items-end gap-px">
        {candles.map((candle, index) => {
          const value = Math.max(0, Number(candle.value) || 0);
          const high = Math.max(0, Number(candle.high) || 0);
          const low = Math.max(0, Number(candle.low) || 0);
          const bodyHeight = value > 0 ? (value / max) * 100 : 0;
          const wickTop = high > 0 ? 100 - (high / max) * 100 : 100;
          const wickBottom = low > 0 ? 100 - (low / max) * 100 : 100;
          const wickHeight = Math.max(0, wickBottom - wickTop);
          return (
            <div
              key={`${candle.label}-${index}`}
              className="relative flex h-full min-w-0 flex-1 items-end"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex((current) => (current === index ? null : current))}
            >
              {high > 0 ? (
                <div
                  className={`pointer-events-none absolute left-1/2 w-px -translate-x-1/2 ${
                    hoveredIndex === index ? 'bg-emerald-700' : 'bg-emerald-500/70'
                  }`}
                  style={{ top: `${wickTop}%`, height: `${Math.max(wickHeight, 1)}%` }}
                />
              ) : null}
              {value > 0 ? (
                <div
                  className={`relative z-[1] w-full min-w-[2px] rounded-t-[2px] bg-gradient-to-t from-emerald-700 to-emerald-400 transition-opacity ${
                    hoveredIndex === index ? 'opacity-100' : hoveredIndex != null ? 'opacity-55' : ''
                  }`}
                  style={{ height: `${bodyHeight}%` }}
                />
              ) : null}
            </div>
          );
        })}
        {hoveredIndex != null && hovered ? (
          <HoverTooltip
            index={hoveredIndex}
            count={candles.length}
            topPercent={hoveredTop}
            bubbleClassName="max-w-sm whitespace-normal"
          >
            <div className="mb-1 flex items-baseline justify-between gap-4">
              <span className="font-bold text-white">
                {chartGranularity === 'hour' ? `${hovered.label}:00` : hovered.label}
              </span>
              <span className="font-mono font-bold text-emerald-300">{formatValue(hoveredValue)}</span>
            </div>
            <div className="text-white/80">
              {hovered.items.length > 0
                ? `${hovered.items.length} ${hovered.items.length === 1 ? 'откуп' : hovered.items.length < 5 ? 'откупа' : 'откупов'}`
                : 'Нет откупов'}
            </div>
            {hovered.details && hovered.details.length > 0 ? (
              <div className="mt-1.5 space-y-0.5 border-t border-white/20 pt-1.5">
                {hovered.details.map((detail) => (
                  <div key={detail.day} className="flex items-center justify-between gap-5 text-white">
                    <span className="font-medium text-white/80">{detail.day}</span>
                    <span className="font-mono font-semibold">{formatValue(detail.value)}</span>
                  </div>
                ))}
              </div>
            ) : null}
            {dateFrom === dateTo && hovered.items.length > 0 ? (
              <div className="vnd-scroll mt-1.5 max-h-40 space-y-1 overflow-y-auto border-t border-white/20 pt-1.5">
                {hovered.items.map((item) => (
                  <div key={item.id} className="font-mono font-medium text-white/95">
                    {buybackTooltipText(item)}
                  </div>
                ))}
              </div>
            ) : null}
          </HoverTooltip>
        ) : null}
      </PlotGrid>
    </ChartShell>
  );
}

